import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  FilePenLine,
  Info,
  Loader2,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import {
  SnipAnchorRect,
  SnipResultPopover,
} from "@/components/FilePreview/SnipResultPopover";
import { useAppForm } from "@/components/Form";
import { MapToReportBuilderDialog } from "@/components/MapToReportBuilderDialog";
import { Transcriber, TranscriberRef } from "@/components/Transcriber";
import {
  TextInsertionOptions,
  useTextareaTranscriptionHandler,
} from "@/components/Transcriber/hooks";
import {
  applyPartialReplacement,
  isPartialRangeIntact,
} from "@/components/Transcriber/partials";
import {
  clearTranscriptSnapshot,
  readTranscriptSnapshot,
} from "@/components/Transcriber/recovery";
import {
  type SpeakerRoleMap,
  buildUnidentifiedSpeakerLabels,
  extendSpeakerRoles,
  resolveSpeakerDisplay,
} from "@/components/Transcriber/segments";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQueryTranscript } from "@/hooks/mutations/use-transcription";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import { useTranscription } from "@/pages/ReportStudio/ReportBuilder/store/useTranscription";
import { setMessage } from "@/redux/notification";
import {
  resumeTranscriptionSummaryTask,
  startTranscriptionSummaryTask,
} from "@/redux/transcription-summary";
import { type RootState, store } from "@/store";

import {
  // AssessmentColumnNameEnum,
  AssessmentResultStatusEnum,
  BodySaveAssessmentApiV1AssessmentPost,
  BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
} from "@api/generated/types.gen";

import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";
import { Button } from "@components/ui/button";
import { Card } from "@components/ui/card";
import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import {
  useCreateAssessment,
  useUpdateAssessmentField,
} from "@hooks/mutations/use-assessments";
import { useAssessmentByCaseNumber } from "@hooks/queries/use-assessments";

import { cn } from "@utils/ui";

import { FETCHING_MESSAGE, NO_CONTENT_MESSAGE } from "../constants/messages";
import { ASSESSMENT_SECTIONS, ASSESSMENT_STATUS } from "../constants/sections";
import { useAutoExport } from "../hooks/useAutoExport";
// import { AcceptButton } from "./AcceptButton";
import { RecordingSession } from "../hooks/useRecordingSession";
import {
  AudioTranscriptFormData,
  createAudioTranscriptSchema,
} from "../schema/audio-transcript";
// import { ExportAssessmentConfirmation } from "./ExportAssessmentConfirmation";
import { buildClearTranscriptFields } from "../utils/transcript";
import { LiveTranscriptionSegment } from "./LiveTranscriptionFeed";
import { RecordingCompleteBanner } from "./RecordingCompleteBanner";
import { ScribeSession, formatDuration } from "./ScribeSession";
import { TranscribeAndUploadButtons } from "./TranscribeAndUploadButtons";
import { TranscriptEntryData, TranscriptEntryList } from "./TranscriptEntry";

/**
 * Type for segmented transcript entry from the API
 * Matches the backend SegmentedTranscriptEntrySchema
 */
type SegmentedTranscriptEntry = {
  speaker: string;
  timestamp: [number, number]; // [start, end] in seconds
  timestamp_hhmmss: [string, string]; // Formatted timestamps
  text: string;
};

/* ─── Plain text content (non-transcript sections + plain mode) ─── */
const PlainTranscriptContent = ({
  isFetching,
  sectionContent,
  isAssessmentNotesSection,
}: {
  isFetching: boolean;
  sectionContent: string;
  isAssessmentNotesSection: boolean;
}) => {
  const isEmpty = !sectionContent || sectionContent === "null";
  return (
    <p
      className={cn(
        "whitespace-pre-wrap text-xs leading-[18px]",
        isEmpty && "italic text-foreground-muted"
      )}
    >
      {isFetching
        ? FETCHING_MESSAGE
        : isEmpty
          ? isAssessmentNotesSection
            ? "You haven't added any notes yet."
            : NO_CONTENT_MESSAGE
          : sectionContent}
    </p>
  );
};

/* ─── Detailed transcript view with search results ─── */
const DetailedTranscriptContent = ({
  isSearching,
  searchError,
  searchResults,
  detailedEntries,
  detailedViewFilter,
  highlightedEntryId,
  onSearchResultClick,
  isLive = false,
  isTranscribing = false,
  isFailed = false,
}: {
  isSearching: boolean;
  searchError: string | null;
  searchResults: {
    answer?: string;
    searchResults?: TranscriptEntryData[];
  } | null;
  detailedEntries: TranscriptEntryData[];
  detailedViewFilter: string;
  highlightedEntryId: string | null;
  onSearchResultClick: (entry: TranscriptEntryData) => void;
  /** A live recording session is in progress (drives the empty state). */
  isLive?: boolean;
  /** Batch transcription is still running (drives the empty state). */
  isTranscribing?: boolean;
  /** Transcription failed (drives the empty state). */
  isFailed?: boolean;
}) => {
  if (isSearching) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-sm text-foreground-muted">
            Searching transcript...
          </span>
        </div>
      </div>
    );
  }

  if (searchError) {
    return (
      <Card className="p-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-sm font-semibold text-destructive">Error</p>
          <p className="text-sm text-foreground-muted">{searchError}</p>
        </div>
      </Card>
    );
  }

  const searchResultEntries = searchResults?.searchResults ?? [];
  const hasSearchResults = searchResultEntries.length > 0;

  return (
    <div className="space-y-6 text-sm">
      {/* Answer */}
      {searchResults?.answer && (
        <Card className="gap-1 space-y-2 p-4">
          <h3 className="text-sm font-semibold text-primary">Answer</h3>
          <div className="border-b border-border" />
          <p
            className="text-sm leading-relaxed text-foreground-muted"
            dangerouslySetInnerHTML={{
              __html: searchResults.answer.replace(
                /\*\*(.*?)\*\*/g,
                "<strong>$1</strong>"
              ),
            }}
          />
        </Card>
      )}

      {/* No answer */}
      {searchResults && !searchResults.answer && (
        <Card className="p-4">
          <p className="text-center text-sm text-foreground-muted">
            No answer found for this query.
          </p>
        </Card>
      )}

      {/* Search results list */}
      {hasSearchResults && (
        <div className="space-y-2 py-2">
          <h3 className="pb-4 text-sm font-semibold text-foreground">
            Search Results ({searchResultEntries.length} found)
          </h3>
          <TranscriptEntryList
            entries={searchResultEntries}
            filter={detailedViewFilter}
            isSearchResults={true}
            onEntryClick={onSearchResultClick}
          />
        </div>
      )}

      {/* Search returned answer but no entries */}
      {searchResults?.answer && !hasSearchResults && (
        <div className="space-y-2 py-2">
          <h3 className="pb-4 text-sm font-semibold text-foreground">
            Search Results (0 found)
          </h3>
          <Card className="p-4">
            <p className="text-center text-sm text-foreground-muted">
              No matching transcript entries found.
            </p>
          </Card>
        </div>
      )}

      {/* Regular entries — no active search */}
      {!searchResults?.searchResults && (
        <>
          {detailedEntries.length > 0 ? (
            <TranscriptEntryList
              entries={detailedEntries}
              filter={detailedViewFilter}
              highlightedEntryId={highlightedEntryId}
            />
          ) : isLive ? (
            <Card className="flex h-full items-center justify-center p-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-primary-50 p-3">
                  <div className="size-3 animate-pulse rounded-full bg-destructive" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    Listening for speech…
                  </p>
                  <p className="max-w-md text-sm text-foreground-muted">
                    Speaker-labelled turns will appear here as the assessment is
                    recorded.
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            /* Not live and nothing to show. Which of the three blurbs applies
               is driven by the assessment/field status — these used to be a
               standalone <AlertBanner> below the panel, which sat far from the
               empty card it was explaining. */
            <Card className="flex h-full items-center justify-center p-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <div
                  className={cn(
                    "rounded-full p-3",
                    isFailed ? "bg-destructive-50" : "bg-muted"
                  )}
                >
                  {isTranscribing ? (
                    <Loader2 className="size-5 animate-spin text-foreground-muted" />
                  ) : isFailed ? (
                    <AlertTriangle className="size-5 text-destructive" />
                  ) : (
                    <Info className="size-5 text-foreground-muted" />
                  )}
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    {isTranscribing
                      ? "Finalising transcript"
                      : isFailed
                        ? "Transcription failed"
                        : "No transcript yet"}
                  </p>
                  <p className="max-w-md text-sm text-foreground-muted">
                    {isTranscribing
                      ? "We're finalising the transcript for this recording. Speaker-labelled turns will appear here shortly."
                      : isFailed
                        ? "We couldn't transcribe this recording. Try uploading the audio again, or record a new session."
                        : "Record or upload audio for this assessment and the speaker-labelled transcript will appear here."}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

type AudioTranscriptProps = {
  caseNumber?: string;
  sectionId?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * The live recording session, read from the store — owned by the
   * root RecordingHost. When active, the content panel shows the
   * in-panel ScribeSession instead of the section editor; when stopped, a
   * completion banner sits above the full-transcript viewer. The diarized
   * summarize on stop is dispatched by the host, not here.
   */
  session?: RecordingSession;
};

export const AudioTranscript = ({
  caseNumber,
  sectionId,
  onDirtyChange,
  session,
}: AudioTranscriptProps) => {
  const dispatch = useDispatch<typeof store.dispatch>();

  // When the AudioRecorder modal owns the session, suppress live writes
  // into this section. The streaming partials still accumulate inside
  // the Transcriber (getFullTranscript) and the Done flow fires
  // /transcription/summarize — that's the only path that should populate
  // the field during a modal-driven recording.
  const isModalSession = useTranscription((s) => s.isModalSession);

  const { data: currentAssessment, isFetching } = useAssessmentByCaseNumber({
    caseNumber,
  });

  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  // The recording-session Transcriber ref comes from the root RecordingHost via
  // the store — used to read the accumulated transcript for the
  // form mirror on session end. Section dictation uses its own local ref.
  const recordingTranscriberRef = useRecordingDock((s) => s.transcriberRef);

  // Ref for the per-section dictation Transcriber. Separate from the
  // recording-session ref so section dictation and the persistent recording
  // never share one Transcriber instance.
  const localTranscriberRef = useRef<TranscriberRef>(null);

  // Current assessment now already includes field-specific statuses
  const assessmentWithFieldStatuses = currentAssessment;

  const updateAssessmentField = useUpdateAssessmentField();
  const createAssessment = useCreateAssessment();

  // Auto-export functionality for assessment fields
  const { triggerAutoExport } = useAutoExport({
    assessmentData: currentAssessment,
    assessmentId: currentAssessment?.id,
    caseNumber: caseNumber || "",
    enabled: !!currentAssessment && !!caseNumber,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [detailedViewFilter, setDetailedViewFilter] = useState<string>("all");
  // Speaker-grouped segments built live from AssemblyAI diarization turns.
  // UI-ONLY: a real-time viewing aid shown while the session is active. They
  // are NOT persisted — the backend's diarized summarize path is the single
  // writer of segmented_full_transcript, which becomes the source of truth
  // once the session ends and the batch result loads.
  const [liveSegments, setLiveSegments] = useState<
    SegmentedTranscriptEntry[] | null
  >(null);
  // Speaker -> role/name display map (e.g. "A" -> "Examiner"). Auto-populated
  // in order of first appearance ONLY from live diarization (see
  // onSegmentsUpdate below) — never auto-applied to the persisted,
  // batch-resolved segmented_full_transcript, whose speaker names are
  // already better than this heuristic. Manual renames (see the "View By"
  // row) work uniformly against whatever raw speaker string is present,
  // live or persisted. See speaker_role_mapping.md, Track A.
  const [speakerRoleMap, setSpeakerRoleMap] = useState<SpeakerRoleMap>({});
  // Which raw speaker label is currently being renamed via the inline
  // edit control, and its in-progress input value. Null when not editing.
  const [editingSpeaker, setEditingSpeaker] = useState<{
    raw: string;
    value: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{
    answer?: string;
    searchResults?: Array<{
      timestamp: string;
      speaker: string;
      speakerType: "doctor" | "patient";
      content: string;
      pauseDuration?: number;
    }>;
  } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [highlightedEntryId, setHighlightedEntryId] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState("");
  // Viewport rect of the highlighted transcript text, so the "Map to Report"
  // popover anchors beside the selection instead of the panel corner.
  const [selectionRect, setSelectionRect] = useState<SnipAnchorRect | null>(null);
  const [mapDialogText, setMapDialogText] = useState("");
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // ── In-panel Scribe session UI state (MDRS-116) ──
  // Assessment Notes are shared with the floating dock via the store and
  // autosaved by the root RecordingHost. The scribe Notes field just
  // reads/writes the same value + reflects the store's save state.
  const sessionNotes = useRecordingDock((s) => s.notes);
  const setSessionNotes = useRecordingDock((s) => s.setNotes);
  const notesSaveState = useRecordingDock((s) => s.notesSaveState);
  const notesLastSavedAt = useRecordingDock((s) => s.notesLastSavedAt);
  // The caret toggles the waveform (supersedes MDRS-85's collapse-to-pill).
  const [waveformCollapsed, setWaveformCollapsed] = useState(false);
  // Latest interim (in-progress) hypothesis for the live feed's greyed line.
  const [livePartial, setLivePartial] = useState("");
  // Stop / Cancel confirmations for the live session controls.
  const [isStopSessionOpen, setIsStopSessionOpen] = useState(false);
  const [isCancelSessionOpen, setIsCancelSessionOpen] = useState(false);
  // Duration snapshot shown on the completion banner (state D).
  const [completedDuration, setCompletedDuration] = useState("00:00:00");

  const isSessionLive = !!session?.isActive;
  const isSessionStopped = !!session?.isStopped;
  const isSessionConnecting = !!session?.isConnecting;

  // When returning to the scribe tab mid-session, this component
  // remounts with empty `liveSegments` — the feed would flash blank until the
  // next turn lands. Seed it once from the always-mounted host Transcriber's
  // accumulated segments so the prior turns show immediately. `isRestoringFeed`
  // covers the instant before that seed (or if the host has no segments yet).
  const [isRestoringFeed, setIsRestoringFeed] = useState(false);
  const feedSeededRef = useRef(false);
  useEffect(() => {
    if (feedSeededRef.current) return;
    if (!isSessionLive) return;
    feedSeededRef.current = true;
    const seeded = recordingTranscriberRef?.current?.getSegments() ?? [];
    if (seeded.length > 0) {
      setLiveSegments(seeded);
    } else {
      // No segments to seed yet — show a restoring affordance until the next
      // turn arrives via onSegmentsUpdate.
      setIsRestoringFeed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionLive]);

  // Notify parent when edit mode changes
  useEffect(() => {
    onDirtyChange?.(isEditing);
  }, [isEditing, onDirtyChange]);

  // Reset all UI state when navigating to a different section. Only the
  // per-section dictation Transcriber is stopped here — the
  // persistent recording session (root RecordingHost) must keep running as the
  // doctor moves between sections.
  useEffect(() => {
    if (localTranscriberRef.current?.isTranscribing) {
      localTranscriberRef.current.stopTranscription();
    }
    setIsEditing(false);
    setDetailedViewFilter("all");
    setSearchQuery("");
    setSearchResults(null);
    setSearchError(null);
    setHighlightedEntryId(null);
    setSelectedText("");
    setMapDialogText("");
    setIsMapDialogOpen(false);
    // Drop any in-memory live segments so they don't bleed into a
    // different section's detailed view (the persisted copy still loads).
    // speakerRoleMap is deliberately NOT reset here — it's assessment-level
    // (persisted, see MDRS-154), not section-level, so switching tabs must
    // not lose a manual rename.
    setLiveSegments(null);
    setEditingSpeaker(null);
  }, [sectionId]);

  // Seed speakerRoleMap from the persisted mapping (MDRS-154) whenever the
  // assessment loads or is refetched. This component isn't remounted across
  // a case switch (no `key` on <AudioTranscript> in AudioTranscriber/index.tsx),
  // so `currentAssessment.id` changing is the only signal that we've moved to
  // a different case's assessment — `prev` at that point belongs to the OLD
  // case and must be replaced, not merged, or a stale rename (e.g. raw label
  // "A") would leak into (and get persisted onto) an unrelated case. Only
  // when the id is unchanged (a refetch of the SAME assessment, e.g. the one
  // our own save in commitSpeakerRename triggers) do local edits win.
  const seededAssessmentIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!currentAssessment?.id) return;
    const persisted = currentAssessment.speaker_role_mapping;
    const isSameAssessment = seededAssessmentIdRef.current === currentAssessment.id;
    seededAssessmentIdRef.current = currentAssessment.id;

    if (isSameAssessment) {
      if (persisted && Object.keys(persisted).length > 0) {
        setSpeakerRoleMap((prev) => ({ ...persisted, ...prev }));
      }
    } else {
      setSpeakerRoleMap(persisted ?? {});
    }
  }, [currentAssessment?.id, currentAssessment?.speaker_role_mapping]);

  // Resume batch-summary polling on load when an assessment is still
  // TRANSCRIBING. The VM result is persisted ONLY inside the status-poll loop,
  // so if the doctor closed the tab / refreshed while it was processing, nothing
  // polls the status endpoint and the summary + diarized transcript are lost
  // (the row stays stuck at TRANSCRIBING). Re-attaching a poller here recovers it.
  // Guards: only if the task isn't already being polled (activeTasks — e.g. the
  // tab that started it), and once per task id (ref) so a poll-driven activeTasks
  // update can't re-dispatch.
  const activeTranscriptionTasks = useSelector(
    (state: RootState) => state.transcription?.activeTasks || {}
  );
  const resumedTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    const taskId = currentAssessment?.task_id;
    if (
      currentAssessment?.status !== "TRANSCRIBING" ||
      !taskId ||
      activeTranscriptionTasks[taskId] ||
      resumedTaskIdRef.current === taskId
    ) {
      return;
    }
    resumedTaskIdRef.current = taskId;
    dispatch(
      resumeTranscriptionSummaryTask({
        taskId,
        caseNumber: caseData?.case_number ?? caseNumber ?? null,
        examineeName: caseData?.examinee_name ?? "",
      })
    );
  }, [
    currentAssessment?.status,
    currentAssessment?.task_id,
    activeTranscriptionTasks,
    caseData?.case_number,
    caseData?.examinee_name,
    caseNumber,
    dispatch,
  ]);

  // Crash-recovery restore (blocker fix): the Transcriber writes the accumulated
  // transcript to localStorage on every turn (saveTranscriptSnapshot) but nothing
  // ever read it back, so a tab/browser crash mid-dictation silently lost up to an
  // hour of clinical text. On mount, if a snapshot exists for THIS page (pathname
  // match) offer to restore it into the transcript, then clear it. The snapshot is
  // cleared on graceful session end, so a leftover one means an ungraceful exit.
  const [recoveredSnapshot, setRecoveredSnapshot] = useState<{
    transcript: string;
    savedAt: number;
  } | null>(null);
  const recoveryCheckedRef = useRef(false);
  useEffect(() => {
    if (recoveryCheckedRef.current) return;
    recoveryCheckedRef.current = true;
    //: the recording session now persists across tab navigation in the
    // root RecordingHost, so returning to the scribe tab REMOUNTS this component
    // while a session is still live (recording/paused/connecting/stopped). In
    // that case the snapshot belongs to the CURRENT session, not a crash — never
    // offer to "restore" it over an in-progress recording. Only a snapshot with
    // no session in flight at all indicates an ungraceful exit. Check both the
    // global store and the session prop so a mount that races the store sync
    // still suppresses the modal.
    const dock = useRecordingDock.getState();
    const sessionInFlight =
      dock.isActive() ||
      dock.status !== "idle" ||
      (!!session && session.status !== "idle");
    if (sessionInFlight) return;
    const snap = readTranscriptSnapshot();
    if (
      snap &&
      snap.transcript.trim() &&
      typeof window !== "undefined" &&
      snap.pathname === window.location.pathname
    ) {
      setRecoveredSnapshot({ transcript: snap.transcript, savedAt: snap.savedAt });
    }
    // Intentionally mount-only: the crash check runs once. `session`/store are
    // read imperatively above (not reactive deps) so a later status change
    // doesn't re-run the check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryTranscript = useQueryTranscript();

  const sectionTitle = useMemo(() => {
    const selectedFeature = ASSESSMENT_SECTIONS.find(
      (item) => item.id === sectionId
    );
    return selectedFeature ? selectedFeature.title : "";
  }, [sectionId]);

  const sectionContent = useMemo(() => {
    const selectedFeature = ASSESSMENT_SECTIONS.find(
      (item) => item.id === sectionId
    );

    if (!selectedFeature || !selectedFeature.key || !currentAssessment) return "";

    // Get the field from assessment
    const field = currentAssessment[selectedFeature.key];

    // Handle the new field structure (which is an object with content and status)
    let content = "";
    if (field) {
      if (typeof field === "object" && "content" in field) {
        // New structure - field is an AssessmentFieldStatusSchema object
        content = field.content;
      } else if (typeof field === "string") {
        // Old structure for backward compatibility
        content = field;
      }
    }

    // Return empty string if content is the literal string "null"
    return content === "null" ? "" : content;
  }, [currentAssessment, sectionId]);

  const sectionKey = useMemo(() => {
    const selectedFeature = ASSESSMENT_SECTIONS.find(
      (item) => item.id === sectionId
    );
    return selectedFeature?.key;
  }, [sectionId]);

  // Get field-specific status if available
  const fieldStatus = useMemo(() => {
    if (!sectionKey || !assessmentWithFieldStatuses) return undefined;

    // Find the field in the assessment with field statuses
    const field =
      assessmentWithFieldStatuses[
        sectionKey as keyof typeof assessmentWithFieldStatuses
      ];

    // If it's an object with a status property, return that status.
    // Cast needed: speaker_role_mapping's index-signature type structurally
    // satisfies `"status" in field` too, so TS can't narrow `field` down to
    // just AssessmentFieldStatusSchema here — sectionKey is always a real
    // report-section key (see ASSESSMENT_SECTIONS), never "speaker_role_mapping".
    if (field && typeof field === "object" && "status" in field) {
      return field.status as AssessmentResultStatusEnum;
    }

    // Otherwise fall back to the overall assessment status
    return currentAssessment?.status;
  }, [assessmentWithFieldStatuses, sectionKey, currentAssessment?.status]);

  const isAssessmentNotes = sectionKey === "notes";

  // Friendly fallback for a LIVE raw speaker id extendSpeakerRoles left
  // unmapped (e.g. a voice detected beyond the session's maxSpeakers cap —
  // see segments.ts). Built from `liveSegments` specifically — never the
  // persisted transcript — so a batch-resolved session's real,
  // backend-assigned speaker names are never overwritten by this fallback.
  const unidentifiedSpeakerLabels = useMemo(
    () => buildUnidentifiedSpeakerLabels(liveSegments ?? [], speakerRoleMap),
    [liveSegments, speakerRoleMap]
  );

  const detailedEntries = useMemo((): TranscriptEntryData[] => {
    // While a session is live, show the in-memory live segments (UI-only
    // viewing aid). Once it ends, fall back to the persisted, batch-diarized
    // segmented_full_transcript so the rough live copy never shadows the
    // higher-quality backend result.
    const rawSegments = isModalSession
      ? (liveSegments ?? currentAssessment?.segmented_full_transcript)
      : currentAssessment?.segmented_full_transcript;
    if (!rawSegments || !Array.isArray(rawSegments) || rawSegments.length === 0) {
      return [];
    }

    const segments = rawSegments as SegmentedTranscriptEntry[];

    return segments.map((segment, index, arr) => {
      const rawSpeaker = segment.speaker || "Unknown";
      const speaker = resolveSpeakerDisplay(
        rawSpeaker,
        speakerRoleMap,
        unidentifiedSpeakerLabels
      );
      const speakerLower = speaker.toLowerCase();
      const speakerType: "doctor" | "patient" =
        speakerLower.includes("dr") ||
        speakerLower.includes("doctor") ||
        speakerLower.includes("physician") ||
        speakerLower.includes("clinician") ||
        speakerLower.includes("examiner")
          ? "doctor"
          : "patient";

      let pauseDuration: number | undefined;
      if (index < arr.length - 1) {
        const currentEnd = segment.timestamp?.[1] || 0;
        const nextStart = arr[index + 1].timestamp?.[0] || 0;
        const gap = nextStart - currentEnd;
        // Only show pause if it's significant (> 0.5 seconds)
        if (gap > 0.5) {
          pauseDuration = gap;
        }
      }

      // Format timestamp - use the start time from timestamp_hhmmss or format from seconds
      // TODO: ensure format in the future, ask backend.
      let timestamp = "0:00";
      if (segment.timestamp_hhmmss?.[0]) {
        // Remove leading hour zero for cleaner display (e.g., "00:05:30" -> "5:30") but keep MM:SS zeros
        timestamp = segment.timestamp_hhmmss[0].replace(/^00:/, "") || "0:00";
      } else if (segment.timestamp?.[0] !== undefined) {
        // Fallback: format seconds to MM:SS
        const totalSeconds = Math.floor(segment.timestamp[0]);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        timestamp = `${minutes}:${seconds.toString().padStart(2, "0")}`;
      }

      return {
        timestamp,
        speaker,
        speakerType,
        content: segment.text || "",
        pauseDuration,
      };
    });
  }, [
    isModalSession,
    liveSegments,
    currentAssessment?.segmented_full_transcript,
    speakerRoleMap,
    unidentifiedSpeakerLabels,
  ]);

  // Unique speakers for the "View By" filter row, as { raw, display } pairs —
  // `raw` is the underlying segment.speaker value (needed to key a rename
  // into speakerRoleMap), `display` is what's shown/filtered on (role- or
  // user-renamed, or a friendly "Unidentified speaker N" fallback for a LIVE
  // voice extendSpeakerRoles couldn't name). Order preserved as first-seen,
  // matching the role-mapping convention (see extendSpeakerRoles).
  const uniqueSpeakers = useMemo((): { raw: string; display: string }[] => {
    const rawSegments = isModalSession
      ? (liveSegments ?? currentAssessment?.segmented_full_transcript)
      : currentAssessment?.segmented_full_transcript;
    if (!rawSegments || !Array.isArray(rawSegments) || rawSegments.length === 0) {
      return [];
    }

    const segments = rawSegments as SegmentedTranscriptEntry[];
    const seen = new Set<string>();
    const result: { raw: string; display: string }[] = [];

    segments.forEach((segment) => {
      const raw = segment.speaker;
      if (raw && !seen.has(raw)) {
        seen.add(raw);
        result.push({
          raw,
          display: resolveSpeakerDisplay(
            raw,
            speakerRoleMap,
            unidentifiedSpeakerLabels
          ),
        });
      }
    });

    return result;
  }, [
    isModalSession,
    liveSegments,
    currentAssessment?.segmented_full_transcript,
    unidentifiedSpeakerLabels,
    speakerRoleMap,
  ]);

  // Commit an in-progress speaker rename (see the "View By" row) into
  // speakerRoleMap. Empty input is treated as a cancel, not a rename to
  // "" — the speaker keeps whatever name it had before editing started.
  const commitSpeakerRename = useCallback(() => {
    if (!editingSpeaker) return;
    const { raw, value } = editingSpeaker;
    const trimmed = value.trim();
    setEditingSpeaker(null);
    if (!trimmed) return;

    const prevDisplay = speakerRoleMap[raw] ?? raw;
    if (prevDisplay === trimmed) return;

    const nextRoleMap = { ...speakerRoleMap, [raw]: trimmed };
    setSpeakerRoleMap(nextRoleMap);
    // If the active filter pointed at this speaker's old display name,
    // follow the rename so the filter doesn't silently start matching
    // nothing.
    if (detailedViewFilter === prevDisplay) {
      setDetailedViewFilter(trimmed);
    }

    // Persist the rename (MDRS-154) so it survives a section switch or a
    // page navigation, not just this render. Fire-and-forget: renames are
    // already gated on blur/Enter (not per-keystroke), so no debounce is
    // needed here. Only manual renames are saved — the live-session
    // heuristic auto-assignment in onSegmentsUpdate below stays local-only.
    if (currentAssessment?.id && currentAssessment?.case_id) {
      updateAssessmentField.mutate({
        assessmentId: currentAssessment.id,
        caseId: currentAssessment.case_id,
        caseNumber,
        field: "speaker_role_mapping",
        value: JSON.stringify(nextRoleMap),
      });
    }
  }, [
    editingSpeaker,
    speakerRoleMap,
    detailedViewFilter,
    currentAssessment,
    caseNumber,
    updateAssessmentField,
  ]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // STP-88: streaming-dictation partials are written directly into the
  // textarea and replaced in place as AssemblyAI refines each turn — so
  // the doctor sees words appear as they speak instead of waiting for
  // end_of_turn. These refs track where the active partial lives.
  // partialStartOffsetRef:  start offset of the in-progress partial in
  //                         the textarea, or null when no turn is open.
  // partialLengthRef:       length of the last partial we wrote, so the
  //                         next partial knows what to replace.
  // lastPartialTextRef:     the exact text we wrote, used to detect
  //                         user-edits inside the range and abandon it
  //                         gracefully if so.
  const partialStartOffsetRef = useRef<number | null>(null);
  const partialLengthRef = useRef<number>(0);
  const lastPartialTextRef = useRef<string>("");

  const resetPartialRange = useCallback(() => {
    partialStartOffsetRef.current = null;
    partialLengthRef.current = 0;
    lastPartialTextRef.current = "";
  }, []);

  const form = useAppForm({
    defaultValues: {
      content: sectionContent || "",
    } as AudioTranscriptFormData,
    validators: {
      onBlur: createAudioTranscriptSchema(isAssessmentNotes),
    },
    onSubmit: async ({ value }) => {
      // Stop the section dictation if it's active — never the persistent
      // recording session.
      if (localTranscriberRef.current?.isTranscribing) {
        localTranscriberRef.current.stopTranscription();
      }

      if (!sectionKey || !caseNumber) {
        return;
      }

      try {
        // Ensure we're not sending 'null' as a string - use empty string instead
        const contentValue =
          value.content === null || value.content === undefined ? "" : value.content;

        if (currentAssessment?.id && currentAssessment?.case_id) {
          await updateAssessmentField.mutateAsync({
            assessmentId: currentAssessment.id,
            caseId: currentAssessment.case_id,
            caseNumber: caseNumber,
            field: sectionKey as keyof Omit<
              BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
              "case_id"
            >,
            value: contentValue,
            status: "EDITED",
          });
        } else {
          // No assessment row exists yet for this case (user is editing
          // a section before any recording/upload has happened). Create
          // one with this field populated so the save isn't lost.
          await createAssessment.mutateAsync({
            case_number: caseNumber,
            [sectionKey]: contentValue,
          } as BodySaveAssessmentApiV1AssessmentPost);
        }

        setIsEditing(false);

        if (sectionKey !== "full_transcript") {
          // Trigger auto-export for fields that are not yet exported
          triggerAutoExport();
        } else {
          if (caseData) {
            const formData = new FormData();
            formData.append("case_number", caseData?.case_number || "");
            formData.append("examinee_name", caseData?.examinee_name || "");
            formData.append("full_transcript", contentValue || "");

            // Start the transcription task using Redux
            await dispatch(startTranscriptionSummaryTask(formData));
          }
        }
      } catch (error) {
        console.error("Failed to update assessment field:", error);
      }
    },
  });

  // Update form values when section content changes — but NOT while the user
  // has unsaved edits in the form (`isEditing`). Otherwise a background refetch
  // that changes `sectionContent` (e.g. a resume-poll completion invalidating
  // the assessment query) silently clobbers in-progress edits — including a
  // just-restored crash-recovery snapshot, re-losing the exact text MDRS-217
  // sets out to protect. Switching sections resets `isEditing` first (the
  // section-nav effect above), so section changes still repopulate; `isEditing`
  // is in the deps so the reset runs the moment editing ends (save/cancel).
  useEffect(() => {
    if (isEditing) return;
    form.reset({
      content: sectionContent || "",
    });
  }, [sectionContent, isEditing, form]);

  // Crash-recovery restore handlers (see recoveredSnapshot mount effect above).
  // Declared after `form` so they can write the recovered text into it.
  const handleRestoreSnapshot = useCallback(() => {
    if (!recoveredSnapshot) return;
    // Never clobber existing content — append the recovered text after it.
    const existing = (form.getFieldValue("content") || "").trim();
    const restored = existing
      ? `${existing}\n\n${recoveredSnapshot.transcript}`
      : recoveredSnapshot.transcript;
    form.setFieldValue("content", restored);
    setIsEditing(true);
    clearTranscriptSnapshot();
    setRecoveredSnapshot(null);
  }, [recoveredSnapshot, form]);

  const handleDiscardSnapshot = useCallback(() => {
    clearTranscriptSnapshot();
    setRecoveredSnapshot(null);
  }, []);

  // Base handler for real-time transcription
  const baseTranscriptionHandler = useTextareaTranscriptionHandler(
    textareaRef,
    (val) => {
      form.setFieldValue("content", val as string | undefined);
    },
    (val) => {
      form.setFieldValue("content", val as string | undefined);
    }
  );

  // STP-88: write each partial directly into the textarea and replace
  // it in place as AssemblyAI refines its hypothesis. Finalise on
  // end_of_turn (which arrives via onTextInsert with `final + " "`).
  //
  // The fallback path — when the textarea isn't mounted, or the user has
  // edited inside our active partial range — defers to the base handler
  // so we don't clobber their typing.
  const transcriptionHandler = {
    ...baseTranscriptionHandler,
    // Speaker-grouped segments from diarized sessions. Independent of the
    // textarea/modal-session plumbing — we always surface them so the
    // detailed (speaker) view updates live as the assessment is recorded.
    onSegmentsUpdate: (
      segments: SegmentedTranscriptEntry[],
      maxSpeakers?: number
    ) => {
      setLiveSegments(segments);
      setIsRestoringFeed(false);
      // A finalised turn just landed — clear the interim line so it isn't
      // duplicated beneath the committed segments (MDRS-116).
      setLivePartial("");
      // Auto-assign roles (by first-appearance order) for any newly-seen raw
      // speaker labels. Only ever applied to LIVE segments — see
      // speakerRoleMap's declaration comment for why persisted,
      // batch-resolved names must never run through this. maxSpeakers caps
      // how many distinct roles get invented, so a diarizer glitch (a
      // short/unclear fragment split into its own phantom speaker id)
      // can't manufacture a fake extra role like "Speaker C" in a session
      // explicitly configured for 2 speakers.
      //
      // The role NAMES come from the consent modal's checked speaker
      // checklist (session.roleOrder — e.g. ["Examiner", "Examinee",
      // "Interpreter"]) so a checked Interpreter/Other gets their real role
      // instead of a generic "Speaker C". Falls back to
      // extendSpeakerRoles's default (Examiner/Examinee) when no session is
      // active yet (e.g. dictation-only "Start dictation" sessions never go
      // through the checklist).
      setSpeakerRoleMap((prev) =>
        extendSpeakerRoles(
          prev,
          segments,
          maxSpeakers,
          session?.roleOrder?.length ? session.roleOrder : undefined
        )
      );
    },
    onPartialUpdate: (partial: string) => {
      // Modal-driven sessions don't write through to the section — the
      // Transcriber still accumulates internally for the Done flow. Capture
      // the interim hypothesis, though, so the in-panel live feed can show the
      // greyed "in-progress" line with a cursor (MDRS-116).
      if (isModalSession) {
        setLivePartial(partial);
        if (partial) setIsRestoringFeed(false);
        return;
      }

      const textarea = textareaRef.current;
      if (!textarea) return;

      if (!isEditing) {
        setIsEditing(true);
      }

      // Empty partial is an "end of partial" hint from the Transcriber.
      // The onTextInsert path will already have committed the final text
      // (or the silence-modal path will have stopped the session), so
      // just close the range.
      if (partial === "") {
        resetPartialRange();
        return;
      }

      const current = textarea.value;

      // First partial of a fresh turn: anchor at the current cursor.
      if (partialStartOffsetRef.current === null) {
        // Bug fix: when the silence-detection modal opens and then
        // closes, Radix restores focus asynchronously. If the first
        // partial after silence arrives before focus has fully
        // returned to the textarea, an unfocused textarea reports
        // `selectionStart === 0` in some browsers — anchoring the
        // next partial at the very start of the text instead of
        // where the doctor was actually dictating. Re-focus first so
        // `selectionStart` reads the real cursor position.
        if (document.activeElement !== textarea) {
          textarea.focus();
        }
        partialStartOffsetRef.current = textarea.selectionStart;
        partialLengthRef.current = 0;
        lastPartialTextRef.current = "";
      }

      const start = partialStartOffsetRef.current;
      const oldLength = partialLengthRef.current;

      // If the user has typed into our partial range, surrender it — the
      // next partial will start a fresh range at the new cursor.
      if (
        !isPartialRangeIntact({
          current,
          start,
          oldLength,
          lastPartial: lastPartialTextRef.current,
        })
      ) {
        resetPartialRange();
        return;
      }

      const { text, newLength } = applyPartialReplacement({
        current,
        start,
        oldLength,
        partial,
      });

      form.setFieldValue("content", text);
      partialLengthRef.current = newLength;
      lastPartialTextRef.current = partial;

      // Restore cursor at end of partial after the form re-renders.
      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        const cursor = start + newLength;
        ta.setSelectionRange(cursor, cursor);
      }, 0);
    },
    onTextInsert: (text: string, eventData: TextInsertionOptions) => {
      // Modal-driven sessions don't write through to the section.
      if (isModalSession) return;

      if (!isEditing) {
        setIsEditing(true);
      }

      const textarea = textareaRef.current;
      // No active partial range OR no textarea — defer to the base
      // handler, which performs the usual cursor-insert.
      if (textarea == null || partialStartOffsetRef.current === null) {
        baseTranscriptionHandler.onTextInsert(text, eventData);
        return;
      }

      const current = textarea.value;
      const start = partialStartOffsetRef.current;
      const oldLength = partialLengthRef.current;

      // User typed into the range while a partial was in flight — defer
      // to the base handler so we don't overwrite their edits.
      if (
        !isPartialRangeIntact({
          current,
          start,
          oldLength,
          lastPartial: lastPartialTextRef.current,
        })
      ) {
        resetPartialRange();
        baseTranscriptionHandler.onTextInsert(text, eventData);
        return;
      }

      // Replace the partial range with the finalised text and close the
      // range. The next partial will anchor at the new cursor position.
      const { text: nextValue } = applyPartialReplacement({
        current,
        start,
        oldLength,
        partial: text,
      });
      form.setFieldValue("content", nextValue);
      resetPartialRange();

      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        const cursor = start + text.length;
        ta.setSelectionRange(cursor, cursor);
        ta.focus();
      }, 0);
    },
    onFocus: () => {
      // Modal-driven sessions shouldn't focus the section textarea or
      // flip it into edit mode — the user is looking at the dialog.
      if (isModalSession) return;

      // Auto-enter edit mode when focusing for transcription
      if (!isEditing) {
        setIsEditing(true);
      }

      // Call the original handler if it exists
      if (baseTranscriptionHandler.onFocus) {
        baseTranscriptionHandler.onFocus();
      }
    },
    onEnd: () => {
      resetPartialRange();
      // If the session ended without committing any text — typically a
      // failed start — undo the optimistic setIsEditing(true) from
      // onFocus. Otherwise the user is left with an editable textarea
      // pre-loaded with the existing transcript, and the next dictation
      // inserts at the cursor instead of replacing the field as the
      // overwrite confirmation implied.
      // Dictation-completion check reads the per-section dictation Transcriber
      //; the recording session's transcript is read separately on
      // stop via the shared store ref.
      const accumulated = localTranscriberRef.current?.getFullTranscript() ?? "";
      if (!accumulated.trim()) {
        setIsEditing(false);
      }
      if (baseTranscriptionHandler.onEnd) {
        baseTranscriptionHandler.onEnd();
      }
    },
  };

  // Feed the host's always-mounted Transcriber this section's live handler
  // so it drives the on-tab live view. `transcriptionHandler` is a
  // fresh object each render, so register a STABLE ref-backed wrapper once and
  // keep the ref current — otherwise the store would churn every render/tick.
  const transcriptionHandlerRef = useRef(transcriptionHandler);
  transcriptionHandlerRef.current = transcriptionHandler;
  const registerLiveCallback = useRecordingDock((s) => s.registerLiveCallback);
  useEffect(() => {
    registerLiveCallback({
      onFocus: () => transcriptionHandlerRef.current.onFocus?.(),
      onEnd: () => transcriptionHandlerRef.current.onEnd?.(),
      onTextInsert: (text, opts) =>
        transcriptionHandlerRef.current.onTextInsert(text, opts),
      onPartialUpdate: (partial) =>
        transcriptionHandlerRef.current.onPartialUpdate?.(partial),
      onSegmentsUpdate: (segments, maxSpeakers) =>
        transcriptionHandlerRef.current.onSegmentsUpdate?.(segments, maxSpeakers),
    });
    return () => registerLiveCallback(null);
  }, [registerLiveCallback]);

  const blurb = useMemo(() => {
    switch (sectionKey) {
      case "full_transcript":
        return (
          <p className="max-w-4xl text-sm text-foreground-muted">
            This is an AI-generated transcript from the assessment recording and may
            contain errors. Summaries are generated based on the content of this
            transcript.
          </p>
        );
      case "notes":
        return (
          <div className="max-w-4xl text-sm text-foreground-muted">
            You can transcribe any additional notes or comments regarding the
            assessment here. 
          </div>
        );
      default:
        return (
          <p className="max-w-4xl text-sm text-foreground-muted">
            Always check the summary for accuracy. You can edit the summary. Click
            Save after editing to send the updated summary to Compose.
          </p>
        );
    }
  }, [sectionKey]);

  const tooltipContent = useMemo(() => {
    switch (sectionKey) {
      case "full_transcript":
        return (
          <section className="space-y-2 text-xs">
            <h1 className="text-sm font-semibold">Full Transcript</h1>
            <p className="text-foreground-muted">
              This is the transcript generated from the assessment recording.
              Summaries are generated based on the content of this transcript.
            </p>
            <p className="font-medium">
              Important: the transcript is AI-generated and may contain errors.
              Details may be misheard, misspelled, omitted, or hallucinated.
            </p>
            <p className="text-foreground-muted">
              Always review and critically assess AI outputs.
            </p>
          </section>
        );
      case "notes":
        return (
          <section className="text-xs">
            <h1 className="text-sm font-semibold">Assessment Notes</h1>
            <p className="text-foreground-muted">
              This section is designed to provide you with a place to record
              additional notes, thoughts and comments. Notes added here are not
              automatically exported to Compose or included in your final report,
              unless you manually add them.
            </p>
          </section>
        );
    }
  }, [sectionKey]);

  const handleCancel = () => {
    // Stop the section dictation if it's active — never the persistent
    // recording session.
    if (localTranscriberRef.current?.isTranscribing) {
      localTranscriberRef.current.stopTranscription();
    }

    form.reset({
      content: sectionContent || "",
    });
    setIsEditing(false);
  };

  const toggleEditing = () => {
    if (isEditing) {
      handleCancel();
    } else {
      setIsEditing(true);
    }
  };

  const isAssessmentNotesSection = sectionKey === "notes";
  const isFullTranscript = sectionKey === "full_transcript";

  // The in-panel live recording view (ScribeSession) belongs to the Transcript
  // section only. On any other section the recording keeps running —
  // surfaced by the floating dock — while that section shows its own content.
  const showLiveView = isSessionLive && isFullTranscript;

  // Publish whether the live Transcript view is on screen so the floating dock
  // hides while it's the surface and shows on other sections.
  const setTranscriptViewActive = useRecordingDock((s) => s.setTranscriptViewActive);
  useEffect(() => {
    setTranscriptViewActive(showLiveView);
    return () => setTranscriptViewActive(false);
  }, [showLiveView, setTranscriptViewActive]);

  // Assessment Notes autosave now lives in the root RecordingHost
  // so it fires from both the scribe Notes field and the floating
  // dock, on or off the scribe tab. This component just reads/writes the shared
  // store value + reflects its save state.

  // Recording session ended → UI reconciliation on the scribe tab.
  // The diarized summarize task is dispatched by the root RecordingHost (so it
  // fires even when finishing from the dock off-tab); here we only reconcile
  // the visible section view when the page IS mounted:
  //   1) exit edit mode so Cancel/Save + the nav warning aren't stuck on,
  //   2) mirror the accumulated transcript into the section field so the
  //      doctor sees it and can edit/save it.
  // (Notes autosave + its flush-on-stop live in the RecordingHost now.)
  const sawStoppedRef = useRef(false);
  useEffect(() => {
    const stopped = session?.status === "stopped";
    if (!stopped) {
      sawStoppedRef.current = false;
      return;
    }
    if (sawStoppedRef.current) return;
    sawStoppedRef.current = true;

    setIsEditing(false);

    const fromTranscriber =
      recordingTranscriberRef?.current?.getFullTranscript() ?? "";
    const fromForm = (form.getFieldValue("content") || "").trim();
    const transcriptContent = fromTranscriber || fromForm;

    if (transcriptContent && transcriptContent !== fromForm) {
      form.setFieldValue("content", transcriptContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  // Scroll to highlighted entry after search results are cleared and regular list re-renders
  useEffect(() => {
    if (!highlightedEntryId) return;
    const el = document.getElementById(highlightedEntryId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const timer = setTimeout(() => setHighlightedEntryId(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightedEntryId]);

  const handleSearchResultClick = (entry: TranscriptEntryData) => {
    const matchIndex = detailedEntries.findIndex(
      (e) => e.content === entry.content && e.speaker === entry.speaker
    );
    setSearchResults(null);
    setSearchQuery("");
    setDetailedViewFilter("all");
    if (matchIndex !== -1) {
      setHighlightedEntryId(`transcript-entry-${matchIndex}`);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !caseNumber) return;

    setSearchError(null);
    setSearchResults(null);
    setHighlightedEntryId(null);

    try {
      const response = await queryTranscript.mutateAsync({
        case_number: caseNumber,
        query: searchQuery.trim(),
      });
      // Handle the response structure - result can be a dict with answer and results
      const result = response.result || {};

      // The API returns "results" (not "search_results" or "searchResults")
      const resultsArray = (result as any).results || [];

      // Transform results to extract segments[0] and map to TranscriptEntryData format
      const transformedResults = resultsArray
        .map((resultItem: any) => {
          // Get the first segment from each result
          const segment = resultItem.segments?.[0];
          if (!segment) return null;

          // Map segment data to TranscriptEntryData format
          return {
            timestamp:
              segment.timestamp_display ||
              (Array.isArray(segment.timestamp)
                ? segment.timestamp.join(":")
                : "") ||
              "",
            speaker: segment.speaker || "Unknown",
            speakerType:
              segment.speaker?.toLowerCase().includes("doctor") ||
              segment.speaker?.toLowerCase().includes("dr") ||
              segment.speaker?.toLowerCase().includes("expert")
                ? ("doctor" as const)
                : ("patient" as const),
            content: segment.text || "",
            pauseDuration: undefined, // Add if available in segment data
          };
        })
        .filter((item: any) => item !== null); // Remove null entries

      setSearchResults({
        answer: typeof result.answer === "string" ? result.answer : undefined,
        searchResults: transformedResults,
      });
    } catch (error) {
      console.error("Failed to search transcript:", error);
      setSearchResults(null);
      setSearchError("Something went wrong, please try again later.");
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
    setSearchError(null);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const templateId = caseData?.template?.id;

  const handleMouseUp = useCallback(() => {
    if (isEditing) return;
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text) {
      setSelectedText(text);
      // Anchor the popover to the selection's bounding rect (viewport coords).
      const rect = selection?.rangeCount
        ? selection.getRangeAt(0).getBoundingClientRect()
        : null;
      setSelectionRect(
        rect && (rect.width || rect.height)
          ? {
              top: rect.top,
              bottom: rect.bottom,
              left: rect.left,
              right: rect.right,
            }
          : null
      );
    }
  }, [isEditing]);

  const handleSelectionClose = useCallback(() => {
    setSelectedText("");
    setSelectionRect(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleMapToReportBuilder = useCallback(() => {
    if (!selectedText) return;
    setMapDialogText(selectedText);
    setSelectedText("");
    setSelectionRect(null);
    window.getSelection()?.removeAllRanges();
    setIsMapDialogOpen(true);
  }, [selectedText]);

  // MDRS-86 — "Delete transcript" blanks the transcript fields rather than
  // hard-deleting the assessment row, so summaries/history are preserved.
  const handleDeleteTranscript = useCallback(async () => {
    if (!currentAssessment?.id || !currentAssessment?.case_id) {
      setIsDeleteDialogOpen(false);
      return;
    }

    try {
      for (const { field, value } of buildClearTranscriptFields()) {
        await updateAssessmentField.mutateAsync({
          assessmentId: currentAssessment.id,
          caseId: currentAssessment.case_id,
          caseNumber,
          field,
          value,
        });
      }
      dispatch(setMessage({ content: "Transcript deleted", type: "success" }));
    } catch {
      // useUpdateAssessmentField logs the underlying error in its onError.
      dispatch(
        setMessage({ content: "Failed to delete transcript", type: "error" })
      );
    } finally {
      setIsDeleteDialogOpen(false);
    }
  }, [currentAssessment, caseNumber, updateAssessmentField, dispatch]);

  // ── In-panel Scribe session wiring (MDRS-116) ──

  // Elapsed session timer. Reads the SINGLE store clock — the same
  // drift-free, pause-aware clock the floating dock shows — so the in-panel and
  // dock timers never diverge. A 1s interval only forces a re-render; the value
  // is always computed from the store's clock.
  const dockElapsedMs = useRecordingDock((s) => s.elapsedMs);
  const [sessionElapsedMs, setSessionElapsedMs] = useState(() => dockElapsedMs());

  useEffect(() => {
    setSessionElapsedMs(dockElapsedMs());
    if (session?.status === "stopped") {
      setCompletedDuration(formatDuration(dockElapsedMs()));
    }
    if (session?.status !== "recording") return;
    const interval = setInterval(() => setSessionElapsedMs(dockElapsedMs()), 1000);
    return () => clearInterval(interval);
  }, [session?.status, dockElapsedMs]);

  // Reset the connecting-time waveform/partial UI (the clock itself is reset in
  // the store by beginSession).
  useEffect(() => {
    if (session?.status === "connecting") {
      setWaveformCollapsed(false);
      setLivePartial("");
    }
  }, [session?.status]);

  // Map the diarized live segments into the feed's shape. Uses the formatted
  // start timestamp where available, falling back to the raw seconds.
  // Speaker resolution mirrors detailedEntries: role/rename map first, then
  // the "Unidentified speaker N" fallback, then the raw id as a last resort
  // — this is the live-recording view (ScribeSession/LiveTranscriptionFeed),
  // so skipping this lookup is exactly why checked roles showed as
  // "Guest-1"/"Guest-2" instead of "Examiner"/"Examinee" here.
  const liveFeedSegments = useMemo((): LiveTranscriptionSegment[] => {
    if (!liveSegments) return [];
    return liveSegments.map((segment) => ({
      speaker: resolveSpeakerDisplay(
        segment.speaker || "Speaker",
        speakerRoleMap,
        unidentifiedSpeakerLabels
      ),
      text: segment.text || "",
      timestamp:
        segment.timestamp_hhmmss?.[0] ??
        (segment.timestamp?.[0] !== undefined
          ? formatDuration(segment.timestamp[0] * 1000)
          : ""),
    }));
  }, [liveSegments, speakerRoleMap, unidentifiedSpeakerLabels]);

  // The interim (in-progress) line: the latest partial hypothesis, attributed
  // to the most recent speaker where one is known.
  const interimSegment = useMemo((): LiveTranscriptionSegment | null => {
    if (!livePartial.trim()) return null;
    const lastRawSpeaker = liveSegments?.[liveSegments.length - 1]?.speaker;
    const speaker = lastRawSpeaker
      ? resolveSpeakerDisplay(
          lastRawSpeaker,
          speakerRoleMap,
          unidentifiedSpeakerLabels
        )
      : "Speaker";
    return {
      speaker,
      text: livePartial,
      timestamp: formatDuration(sessionElapsedMs),
    };
  }, [
    livePartial,
    liveSegments,
    sessionElapsedMs,
    speakerRoleMap,
    unidentifiedSpeakerLabels,
  ]);

  const handleSessionStopConfirm = useCallback(() => {
    setIsStopSessionOpen(false);
    session?.stop();
  }, [session]);

  const handleSessionCancelConfirm = useCallback(() => {
    setIsCancelSessionOpen(false);
    session?.cancel();
  }, [session]);

  return (
    <>
      {showLiveView && (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <Card
            as="section"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-border bg-background-white p-0"
          >
            <ScribeSession
              isPaused={!!session?.isPaused}
              elapsedMs={sessionElapsedMs}
              mediaRecorder={session?.mediaRecorder ?? null}
              inputDeviceLabel={session?.inputDeviceLabel ?? "Default microphone"}
              segments={liveFeedSegments}
              interim={interimSegment}
              isRestoringFeed={isRestoringFeed}
              notes={sessionNotes}
              onNotesChange={setSessionNotes}
              notesSaveState={notesSaveState}
              notesLastSavedAt={notesLastSavedAt}
              waveformCollapsed={waveformCollapsed}
              onToggleWaveform={() => setWaveformCollapsed((prev) => !prev)}
              onPause={() => session?.pause()}
              onResume={() => session?.resume()}
              onStop={() => setIsStopSessionOpen(true)}
              onCancel={() => setIsCancelSessionOpen(true)}
            />
          </Card>

          <ConfirmationModal
            type="warning"
            open={isStopSessionOpen}
            onOpenChange={setIsStopSessionOpen}
            icon={<AlertTriangle className="size-8 text-destructive" />}
            title="Stop recording?"
            description="Once you stop the recording, the session will end and cannot be resumed. All recordings will stop, and the transcription will end."
            cancelButtonText="Cancel"
            confirmButtonText="Stop recording"
            onCancel={() => setIsStopSessionOpen(false)}
            onConfirm={handleSessionStopConfirm}
          />

          <ConfirmationModal
            type="warning"
            open={isCancelSessionOpen}
            onOpenChange={setIsCancelSessionOpen}
            icon={<AlertTriangle className="size-8 text-destructive" />}
            title="Cancel recording?"
            description="If you cancel now, all ongoing recordings will be permanently deleted and cannot be recovered."
            cancelButtonText="Continue Recording"
            confirmButtonText="Delete recording"
            onCancel={() => setIsCancelSessionOpen(false)}
            onConfirm={handleSessionCancelConfirm}
          />
        </div>
      )}

      <div
        className={cn(
          "relative flex min-h-0 flex-1 flex-col gap-4 overflow-hidden",
          showLiveView && "hidden"
        )}
      >
        {/* The Full-Transcript session Transcriber now lives in the root
            RecordingHost so the Azure stream survives tab-nav; this
            page drives it via the shared `transcriberRef` from the store and
            feeds it the live callback (registered above). Nothing to mount
            here anymore. */}

        {/* Completion banner (MDRS-116, state D) — sits above the reused
            full-transcript viewer once a session stops. */}
        {isSessionStopped && isFullTranscript && (
          <RecordingCompleteBanner
            duration={completedDuration}
            onDismiss={() => session?.reset()}
          />
        )}

        {/* Zero state (MDRS-113) — the Full Transcript panel before anything is
            recorded/uploaded. Replaces the panel body with a centered mic-pill,
            copy, and the Start Session / Upload Recording CTAs (which reuse the
            same session-start + upload flow as the header). Suppressed while a
            session is connecting/live/just-stopped, or once a transcript exists. */}
        {session &&
        isFullTranscript &&
        !isEditing &&
        !isModalSession &&
        !isSessionStopped &&
        !isSessionConnecting &&
        !sectionContent ? (
          <Card
            as="section"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-border bg-background-white p-0"
          >
            <TranscribeAndUploadButtons
              caseNumber={caseNumber}
              session={session}
              variant="empty-state"
            />
          </Card>
        ) : (
          <Card
            as="section"
            className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden rounded-[10px] border border-border bg-background-white p-0"
          >
            {/* Header (MDRS-114 — redesigned "rh" bar). Fill is the
              `background-raised` token (Figma #f8f9fc) — a near-white
              blue-grey, a hair lighter than the page background. */}
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-background-raised px-5 py-3.5">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h1 className="text-sm font-semibold text-foreground">
                    {sectionTitle}
                  </h1>
                  {tooltipContent && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="size-4 cursor-help text-foreground-muted" />
                      </TooltipTrigger>
                      <TooltipContent
                        className="max-w-sm text-wrap p-3"
                        side="bottom"
                        align="start"
                      >
                        {tooltipContent}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {blurb}
              </div>

              <div className="flex items-center gap-2">
                {/* Visible dictation button for the non-transcript sections.
                    Uses its OWN local ref — the shared store ref is
                    reserved for the recording-session Transcriber in the root
                    RecordingHost, so per-section dictation and the persistent
                    recording never fight over one Transcriber instance. */}
                {!isFullTranscript && (
                  <Transcriber
                    ref={localTranscriberRef as React.Ref<TranscriberRef>}
                    callback={transcriptionHandler}
                    iconSize="lg"
                    showLabel
                    label="Start dictation"
                  />
                )}

                {isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={updateAssessmentField.isPending}
                  >
                    Cancel
                  </Button>
                )}

                {/* Edit/Save affordance. Hidden on the Full Transcript section —
                  it's read-only (the AI/recording output isn't hand-edited
                  inline; use Delete to clear it). Also hidden while a recording
                  session is live (the section shows via the collapsed pill). */}
                {!isFullTranscript && (!isModalSession || isEditing) && (
                  <Button
                    variant={isEditing ? "default" : "outline"}
                    size="sm"
                    className="flex items-center gap-2"
                    isLoading={updateAssessmentField.isPending}
                    onClick={isEditing ? () => form.handleSubmit() : toggleEditing}
                  >
                    {isEditing ? (
                      updateAssessmentField.isPending ? (
                        "Saving"
                      ) : (
                        "Save"
                      )
                    ) : (
                      <>
                        {!updateAssessmentField.isPending && <FilePenLine />}
                        Edit
                      </>
                    )}
                  </Button>
                )}

                {isFullTranscript &&
                  !isEditing &&
                  !isModalSession &&
                  !!sectionContent && (
                    <Button
                      variant="destructive"
                      size="compact"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      Delete
                    </Button>
                  )}
              </div>
            </div>

            {/* Search + Speaker Filter — full transcript, read mode only.
              Horizontal padding matches the header bar above (px-5 / 16→20px in
              Figma this panel is 16px; we align to the shared header) and the
              gaps mirror the Figma "search-row" / "filter-row" spec: 8px between
              the input and Search button, ~10px between filter chips. */}
            {isFullTranscript && !isEditing && (
              <div className="shrink-0 space-y-3 px-5 py-3 text-sm">
                {/* Search queries the persisted transcript — hide it during a
                  live session (the transcript hasn't been saved yet). The
                  speaker filter below stays available for the live segments. */}
                {!isModalSession && (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyPress}
                        placeholder="Search transcript..."
                        rightIcon={<Search className="size-4" />}
                        className={cn("w-full bg-white", searchQuery && "pr-10")}
                      />
                      {searchQuery && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearSearch();
                          }}
                          className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 bg-white p-0"
                          aria-label="Clear search"
                        >
                          <X className="size-4" />
                        </Button>
                      )}
                    </div>
                    <Button
                      onClick={handleSearch}
                      disabled={!searchQuery.trim() || queryTranscript.isPending}
                      isLoading={queryTranscript.isPending}
                      size="sm"
                    >
                      Search
                    </Button>
                  </div>
                )}

                <div className="flex items-center gap-2.5">
                  <span className="text-foreground-muted">View By:</span>
                  <label className="flex cursor-pointer items-center gap-1.5">
                    <Checkbox
                      checked={detailedViewFilter === "all"}
                      onCheckedChange={() => setDetailedViewFilter("all")}
                    />
                    <span>All</span>
                  </label>
                  {uniqueSpeakers.map(({ raw, display }) => (
                    <div key={raw} className="flex items-center gap-1">
                      <label className="flex cursor-pointer items-center gap-1.5">
                        <Checkbox
                          checked={detailedViewFilter === display}
                          onCheckedChange={() => setDetailedViewFilter(display)}
                        />
                        {editingSpeaker?.raw === raw ? (
                          <Input
                            autoFocus
                            aria-label={`Edit name for ${display}`}
                            value={editingSpeaker.value}
                            onChange={(e) =>
                              setEditingSpeaker({ raw, value: e.target.value })
                            }
                            onBlur={commitSpeakerRename}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                commitSpeakerRename();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                setEditingSpeaker(null);
                              }
                            }}
                            className="h-6 w-28 px-1 text-sm"
                          />
                        ) : (
                          <span>{display}</span>
                        )}
                      </label>
                      {/* Rename affordance — role/speaker labels are a
                          heuristic (see speaker_role_mapping.md, Track A);
                          let the user correct a wrong guess. */}
                      <button
                        type="button"
                        onClick={() => setEditingSpeaker({ raw, value: display })}
                        className="text-foreground-muted hover:text-foreground"
                        aria-label={`Rename ${display}`}
                      >
                        <Pencil className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content Area. Full Transcript is read-only (no inline edit), so
              it always renders the detailed read view below. */}
            {isEditing && !isFullTranscript ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border">
                <form.AppForm>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      form.handleSubmit();
                    }}
                    className="flex h-full flex-col"
                  >
                    <form.AppField name="content">
                      {(field) => (
                        <field.TextAreaField
                          inputRef={textareaRef}
                          placeholder="Enter content for this section..."
                          className="h-full min-h-0 flex-1 resize-none rounded-none border-none bg-background-muted/40 p-4 !leading-loose"
                          wrapperClassName="flex h-full flex-col"
                          childrenContainerClassName="flex-1 flex flex-col"
                          disabled={updateAssessmentField.isPending}
                        />
                      )}
                    </form.AppField>
                  </form>
                </form.AppForm>
              </div>
            ) : (
              <ScrollArea
                className={cn(
                  // Radix ScrollArea scrolls inside its own Viewport, so the Root
                  // must be height-bounded (flex-1 + min-h-0) and clip its
                  // overflow — never `overflow-y-auto`, which lets the Root grow
                  // to content height and breaks scrolling. Padding lives on the
                  // inner wrapper so it doesn't inflate the Viewport size.
                  "min-h-0 flex-1 overflow-hidden",
                  !isFullTranscript && "border-t border-border"
                )}
                onMouseUp={handleMouseUp}
              >
                <div className="p-4">
                  {/* Plain view — non-transcript sections only */}
                  {!isFullTranscript && (
                    <PlainTranscriptContent
                      isFetching={isFetching}
                      sectionContent={sectionContent}
                      isAssessmentNotesSection={isAssessmentNotesSection}
                    />
                  )}

                  {/* Detailed view — full transcript only (MDRS-86: sole view) */}
                  {isFullTranscript && (
                    <DetailedTranscriptContent
                      isSearching={queryTranscript.isPending}
                      searchError={searchError}
                      searchResults={searchResults}
                      detailedEntries={detailedEntries}
                      detailedViewFilter={detailedViewFilter}
                      highlightedEntryId={highlightedEntryId}
                      onSearchResultClick={handleSearchResultClick}
                      isLive={isModalSession}
                      isTranscribing={
                        (fieldStatus || currentAssessment?.status) ===
                        ASSESSMENT_STATUS.TRANSCRIBING
                      }
                      isFailed={
                        (fieldStatus || currentAssessment?.status) ===
                        ASSESSMENT_STATUS.FAILED
                      }
                    />
                  )}
                </div>
              </ScrollArea>
            )}
          </Card>
        )}
      </div>

      {/* Crash-recovery: offer to restore an unsaved dictation snapshot left
          behind by an ungraceful exit (tab/browser crash mid-session). */}
      <ConfirmationModal
        type="info"
        icon={<RotateCcw className="size-8 text-primary" />}
        title="Restore unsaved dictation?"
        description="We found dictation from a previous session on this case that wasn't saved — likely from a tab or browser that closed mid-recording. Restore it into the transcript so you don't lose it?"
        confirmButtonText="Restore"
        cancelButtonText="Discard"
        open={!!recoveredSnapshot}
        onOpenChange={(open) => {
          if (!open) handleDiscardSnapshot();
        }}
        onConfirm={handleRestoreSnapshot}
        onCancel={handleDiscardSnapshot}
      />

      {/* MDRS-86 — confirm before clearing the transcript. */}
      <ConfirmationModal
        type="warning"
        icon={<Trash2 className="size-10 text-destructive" />}
        title="Delete transcript?"
        description="This permanently removes the transcript for this assessment. Summaries already generated are kept. This cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        isLoading={updateAssessmentField.isPending}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteTranscript}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />

      {selectedText && (
        <SnipResultPopover
          title="Selected Text"
          text={selectedText}
          isLoading={false}
          isProcessing={false}
          error={null}
          anchorRect={selectionRect}
          onClose={handleSelectionClose}
          onMapToReportBuilder={handleMapToReportBuilder}
        />
      )}

      <MapToReportBuilderDialog
        open={isMapDialogOpen}
        onOpenChange={setIsMapDialogOpen}
        text={mapDialogText}
        targetField="summary_from_assessment_scribe"
        caseNumber={caseNumber}
        templateId={templateId}
        extractType="highlight"
        categorySlug={sectionKey}
      />
    </>
  );
};
